import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { MarkdownFeld } from '../components/MarkdownFeld'
import { Topbar } from '../components/Topbar'
import { Aktivitaetsfeed } from '../components/Aktivitaetsfeed'
import { AnfragenListe, FrageStellen } from '../components/Anfragen'
import { AufgabenVerweis } from '../components/AufgabeKontext'
import { WartendeAbnahmen } from '../components/WartendeAbnahmen'
import { Button, Card, CardPad, SectionTitle } from '../components/ui/primitives'
import { abnahmenQuery, sitzungQuery, vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { nebenBestandVerwerfen } from '../lib/mutationen'

/**
 * **Das Work-Formular für Fachbereiche** (Phase 6, Konzept Kap. 4.1 – vierter Eingangskanal).
 *
 * ## Warum es das neben „+ Aufgabe" gibt
 *
 * „+ Aufgabe" legt Arbeit an, die jemand **selbst tun will**. Dieses Formular meldet einen
 * Bedarf **an das Team** – die Aufgabe landet im Eingang, nicht in einem Pool, und niemand hat
 * sie sich damit vorgenommen.
 *
 * ## Warum die Fragen so dastehen
 *
 * Am 03.08.2026 gemessen: **18 von 65** Aufgaben im Eingang brauchten eine Rückfrage, weil
 * Titel und Text zusammen zu wenig hergaben ([E-69](../../../../docs/ENTSCHEIDUNGEN.md)).
 * Titel wie *„Weitere Kacheln"* oder *„Datenbank und Model"*.
 *
 * > **Eine Frage beim Melden geht an jemanden, der die Antwort gerade weiß.** Dieselbe Frage
 * > zwei Wochen später geht an jemanden, der sich erinnern muss.
 *
 * Deshalb stehen die drei Fragen als Platzhalter im Textfeld, und deshalb **weist der Server
 * ab**, wenn zusammen zu wenig dasteht – mit derselben Funktion, die den Eingang markiert.
 * Ein Formular, das alles annimmt und hinterher markiert, verschiebt die Arbeit nur.
 */
export function Melden() {
  const queryClient = useQueryClient()
  const { data: sitzung } = useQuery(sitzungQuery)
  const rolle = sitzung?.role ?? 'entwickler'
  const [titel, setTitel] = useState('')
  const [beschreibung, setBeschreibung] = useState('')
  const [bereich, setBereich] = useState('')
  const [prioritaet, setPrioritaet] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [gemeldet, setGemeldet] = useState(false)

  const melden = useMutation({
    mutationFn: () =>
      trpc.eingang.melden.mutate({
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        ...(bereich.trim() ? { bereich: bereich.trim() } : {}),
        ...(prioritaet ? { prioritaet: prioritaet as 'A' } : {}),
      }),
    onSuccess: async () => {
      setGemeldet(true)
      setTitel('')
      setBeschreibung('')
      setBereich('')
      setPrioritaet('')
      setFehler(null)
      await queryClient.invalidateQueries({ queryKey: ['eingang'] })
      await nebenBestandVerwerfen(queryClient)
    },
    onError: (e: Error) => {
      setGemeldet(false)
      setFehler(e.message)
    },
  })

  return (
    <>
      <Topbar
        titel="Mein Bereich"
        untertitel="Etwas melden – es geht in den Eingang, das Team sortiert es ein"
      />

      {gemeldet && (
        <Card className="mb-3">
          <CardPad className="text-[12.5px]">
            <strong className="text-accent-deep">Angekommen.</strong> Die Meldung liegt im Eingang.
            Sie ist damit <em>nicht</em> zugesagt – jemand sieht sie sich an und ordnet sie ein.
          </CardPad>
        </Card>
      )}

      <Aktivitaetsfeed />

      <MeineVorhaben />

      {/**
       * **Fragen vor Melden** (Phase 9, Kap. 4.8). Die häufigere Handlung steht oben: die
       * meisten Anliegen aus einem Fachbereich sind Fragen nach dem Stand, nicht neue
       * Arbeit – und wer erst eine Aufgabe meldet und dann erfährt, dass es sie schon gibt,
       * hat sich und dem Eingang die Arbeit umsonst gemacht.
       */}
      <FrageStellen />
      <AnfragenListe role={rolle} />

      <WartendeAbnahmen />

      <ErteilteAbnahmen />

      <SectionTitle hint="Geht in den Eingang – das Team sortiert es ein">
        Etwas melden
      </SectionTitle>
      <Card>
        <CardPad>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              setFehler(null)
              melden.mutate()
            }}
          >
            <label className="block text-[11.5px] font-semibold text-ink2">
              Worum geht es?
              <input
                value={titel}
                onChange={(e) => setTitel(e.target.value)}
                required
                autoFocus
                placeholder={
                  'In einem Satz \u2013 nicht \u201eFehler\u201c oder \u201eWeitere Kacheln\u201c'
                }
                className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
              />
            </label>

            <div className="mt-3 text-[11.5px] font-semibold text-ink2">
              Was genau passiert?
              <div className="mt-1 font-normal">
                {/**
                 * **Die drei Fragen stehen im Feld, nicht darüber.** Eine Erklärung über dem
                 * Feld liest man einmal; ein Platzhalter steht da, während man tippt.
                 */}
                <MarkdownFeld
                  wert={beschreibung}
                  setzen={setBeschreibung}
                  zeilen={7}
                  platzhalter={
                    'Was passiert, und seit wann?\n' +
                    'Was sollte stattdessen passieren?\n' +
                    'Wen oder was betrifft es – ein Kunde, ein Vorgang, eine Zahl?'
                  }
                />
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              <label className="block text-[11.5px] font-semibold text-ink2">
                Bereich <span className="font-normal text-muted">optional</span>
                <input
                  value={bereich}
                  onChange={(e) => setBereich(e.target.value)}
                  placeholder="z. B. Kundenportal, Abrechnung"
                  className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
                />
              </label>

              <label className="block text-[11.5px] font-semibold text-ink2">
                Dringlichkeit <span className="font-normal text-muted">optional</span>
                <select
                  value={prioritaet}
                  onChange={(e) => setPrioritaet(e.target.value)}
                  className="mt-1 w-full rounded-[9px] border border-line2 bg-surface px-[11px] py-2 text-[13px] font-normal"
                >
                  <option value="">keine Angabe</option>
                  <option value="A">A – blockiert die Arbeit</option>
                  <option value="B">B – stört, geht aber weiter</option>
                  <option value="C">C – wäre schön</option>
                </select>
              </label>
            </div>

            {fehler && (
              <p
                role="alert"
                className="mt-3 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]"
              >
                {fehler}
              </p>
            )}

            <div className="mt-3 flex items-center gap-2.5">
              <Button type="submit" variant="primary" disabled={melden.isPending}>
                {melden.isPending ? 'wird gemeldet …' : 'Melden'}
              </Button>
              <span className="text-[11px] text-muted">
                Landet im Eingang, nicht auf einem Tisch. Markdown ist erlaubt.
              </span>
            </div>
          </form>
        </CardPad>
      </Card>

      {/**
       * **Was diese Ansicht noch werden soll.** Sie stand bis heute als reiner Platzhalter da;
       * das Formular ist der erste Teil davon, der trägt. Der Rest steht im Phasenplan – und
       * ihn hier zu nennen ist ehrlicher, als eine halbe Ansicht wie eine fertige aussehen zu
       * lassen.
       */}
      <p className="mt-3 text-[11.5px] text-muted">
        Später kommen hier eigene Vorhaben, Ein-Klick-Abnahme und der Verlauf der eigenen Meldungen
        dazu (Phase 9).
      </p>
    </>
  )
}

/**
 * **Was auf Abnahme wartet** (Phase 9 – schließt die Lücke aus Phase 2, AP 2.3).
 *
 * ## Warum das hier steht und nicht in „Mein Tag"
 *
 * Eine Abnahme wartet auf **jemand anderen** als den Bearbeiter. In „Mein Tag" sähe sie nur der,
 * der die Arbeit gemacht hat – und der kann sie nicht abnehmen.
 *
 * ## Warum es das überhaupt braucht
 *
 * Gemessen am 05.08.2026: **12 Aufgaben stehen auf „Review/Abnahme", die älteste seit 639
 * Tagen.** Das ist keine Nachlässigkeit. „Review" ist ein Status wie jeder andere; in „Alle
 * Aufgaben" steht er zwischen 3.100 Zeilen. **Es hat sie niemand gesehen, weil es dafür keine
 * Ansicht gab.**
 *
 * Deshalb steht die Wartezeit in Tagen dran und nicht das Datum: „639 Tage" ist eine Aussage,
 * „08.11.2024" ist eine Angabe.
 */
/**
 * **Der Fortschritt der eigenen Vorhaben** (Phase 9, Konzept Kap. 5.8, Mockup `v-bereich`).
 *
 * Die erste Frage eines Abteilungsleiters lautet nicht „was kann ich abnehmen", sondern
 * *„wie steht es um meine Sachen"*. Deshalb steht das hier oben – vor den Abnahmen, die eine
 * Handlung verlangen, und vor dem Formular, das eine neue Sache anfängt.
 *
 * Der Server liefert einem Mit-Owner ohnehin nur seine Vorhaben (`vorhabenSichtbarkeit`).
 * Diese Ansicht filtert nichts nach – sie zeigt, was sie bekommt. Für das Team sind das alle,
 * und auch das ist richtig: dann ist es die Übersicht, die es in der Vorhaben-Ansicht auch
 * hat, nur kürzer.
 */
function MeineVorhaben() {
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const aktive = vorhaben.filter((v) => v.aktiv)
  if (aktive.length === 0) return null

  return (
    <>
      <SectionTitle hint="Was davon ist erledigt, was liegt noch an">Meine Vorhaben</SectionTitle>
      <div className="mb-4 grid gap-2">
        {aktive.map((v) => {
          const anteil = v.stand.gesamt > 0 ? v.stand.erledigt / v.stand.gesamt : 0
          return (
            <Card key={v.id}>
              <CardPad>
                <div className="flex flex-wrap items-baseline gap-2 text-[12.5px]">
                  <strong>{v.label}</strong>
                  {v.zieltermin && (
                    <span className="text-ink2">
                      Ziel{' '}
                      {new Date(v.zieltermin).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })}
                    </span>
                  )}
                  <span className="tabular ml-auto text-[11.5px] text-muted">
                    {v.stand.erledigt} von {v.stand.gesamt} erledigt · {v.stand.offen} offen
                  </span>
                </div>
                <div className="mt-1.5 h-[6px] overflow-hidden rounded-full bg-accent-soft">
                  <i
                    className="block h-full rounded-full bg-accent"
                    style={{ width: `${anteil * 100}%` }}
                  />
                </div>
                {/**
                 * **Ein leeres Vorhaben sagt nichts über Fortschritt** – der Balken stünde auf
                 * null und läse sich wie „nichts geschafft". Er ist noch nicht angefangen.
                 */}
                {v.stand.gesamt === 0 && (
                  <p className="mt-1 text-[11px] text-muted">
                    Noch keine Aufgaben zugeordnet – der Balken sagt hier nichts.
                  </p>
                )}
              </CardPad>
            </Card>
          )
        })}
      </div>
    </>
  )
}

/**
 * **Die Rückschau: was abgenommen wurde, und was dabei gesagt wurde** (Nils, 17.08.2026).
 *
 * *„Mir im Nachhinein die abgenommenen mit Notizen wieder anzeigen können."*
 *
 * ## Warum standardmäßig nur die mit Notiz
 *
 * Weil die anderen nichts erzählen. Eine Abnahme ohne Notiz steht bereits im Verlauf der
 * Aufgabe; hier hereinzuholen, was ohnehin dort steht, macht die Liste lang und
 * uninteressant. Der Umschalter zeigt trotzdem alle – **und die Differenz ist selbst eine
 * Auskunft**: wie oft nimmt jemand ab, ohne etwas dazu zu sagen?
 */
function ErteilteAbnahmen() {
  const [nurMitNotiz, setNurMitNotiz] = useState(true)
  const { data: abnahmen = [] } = useQuery(abnahmenQuery(nurMitNotiz))
  const { data: alle = [] } = useQuery(abnahmenQuery(false))

  if (alle.length === 0) return null

  const mitNotiz = alle.filter((a) => a.notiz !== null).length

  return (
    <>
      <SectionTitle hint="Die letzten 180 Tage – aus dem Ereignis-Log, nicht aus einer zweiten Liste">
        Erteilte Abnahmen
      </SectionTitle>
      <Card className="mb-4">
        <CardPad>
          <div className="mb-2 flex flex-wrap items-baseline gap-2 text-[11.5px] text-muted">
            <strong className="text-[12.5px] text-ink">{alle.length} abgenommen</strong>
            <span>
              davon {mitNotiz} mit Notiz
              {alle.length > mitNotiz ? ` · ${alle.length - mitNotiz} ohne` : ''}
            </span>
            <button
              type="button"
              onClick={() => setNurMitNotiz((v) => !v)}
              className="ml-auto cursor-pointer text-accent-deep hover:underline"
            >
              {nurMitNotiz ? 'alle zeigen' : 'nur mit Notiz'}
            </button>
          </div>

          {abnahmen.length === 0 ? (
            <p className="text-[12.5px] text-ink2">
              Keine Abnahme mit Notiz. Die Notiz ist freiwillig – sie steht beim Abnehmen unter „+
              Notiz".
            </p>
          ) : (
            abnahmen.slice(0, 60).map((a) => (
              <div
                key={`${a.taskId}${String(a.am)}`}
                className="border-b border-line py-1.5 last:border-b-0"
              >
                <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                  <span className="tabular w-[62px] shrink-0 text-muted">
                    {new Date(a.am).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: '2-digit',
                    })}
                  </span>
                  <AufgabenVerweis
                    aufgabeId={a.taskId}
                    className="min-w-0 flex-1 cursor-pointer truncate text-left hover:underline"
                    title={a.titel}
                  >
                    {a.titel}
                  </AufgabenVerweis>
                  <span className="shrink-0 text-[11px] text-muted">
                    {a.vonName ?? 'unbekannt'}
                    {a.vorhabenLabel ? ` · ${a.vorhabenLabel}` : ''}
                  </span>
                </div>
                {/**
                 * **Die Notiz steht unter der Zeile, nicht dahinter.** Sie ist der Grund,
                 * aus dem diese Liste existiert – abgeschnitten in einer Spalte wäre sie
                 * genau das, was sie nicht sein soll: eine Randbemerkung.
                 */}
                {a.notiz && (
                  <p className="mt-1 border-l-2 border-accent-soft pl-2 text-[12px] break-words whitespace-pre-wrap text-ink2">
                    {a.notiz}
                  </p>
                )}
              </div>
            ))
          )}
        </CardPad>
      </Card>
    </>
  )
}
